import React, { useState, useEffect, useCallback } from 'react';
import {
    Card,
    Form,
    InputNumber,
    Select,
    DatePicker,
    Switch,
    Button,
    Row,
    Col,
    Space,
    Tag,
    message,
    Divider,
    Statistic,
    Collapse,
    Tooltip,
    Alert,
    Upload,
    List,
    Modal,
    Progress,
} from 'antd';
import {
    SaveOutlined,
    InfoCircleOutlined,
    CheckCircleOutlined,
    WarningOutlined,
    CloseCircleOutlined,
    CaretRightOutlined,
    DashboardOutlined,
    TrophyOutlined,
    RiseOutlined,
    SafetyOutlined,
    UploadOutlined,
    FileTextOutlined,
    DeleteOutlined,
    EyeOutlined,
    PaperClipOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import apiClient, { handleApiError } from '../utils/axiosConfig';
import ShipmentLogisticsScoreFields, { getShipmentLogisticsScore } from '../components/ShipmentLogisticsScoreFields';

const { Panel } = Collapse;
const { Option } = Select;

const SupplierPerformanceDataEntry = () => {
    const { t } = useTranslation();
    const [form] = Form.useForm();

    // States
    const [suppliers, setSuppliers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [calculatedValues, setCalculatedValues] = useState(null);
    const [activeKey, setActiveKey] = useState(['1', '2', '3', '4', '5', '6', '7']);
    const [selectedSupplier, setSelectedSupplier] = useState(null);
    const [supplierCertificates, setSupplierCertificates] = useState(null);
    const [uploadedFiles, setUploadedFiles] = useState({});
    const [uploadingField, setUploadingField] = useState(null);
    const [loadingCertificates, setLoadingCertificates] = useState(false);

    // ✅ Fetch suppliers on mount
    const fetchSuppliers = useCallback(async () => {
        try {
            console.log('🔄 Fetching suppliers...');
            const response = await apiClient.get('/api/Suppliers/suppliers-only');
            const suppliersData = response.data || [];
            console.log('✅ Suppliers loaded:', suppliersData.length);
            setSuppliers(suppliersData);

            if (suppliersData.length === 0) {
                message.warning(t('no_suppliers_available'));
            }
        } catch (error) {
            const errorMessage = handleApiError(error, t('fetch_suppliers_error'));
            message.error(errorMessage);
            console.error('❌ Error fetching suppliers:', error);
        }
    }, [t]);

    useEffect(() => {
        fetchSuppliers();
    }, [fetchSuppliers]);

    // ✅ Fetch supplier certificates when supplier is selected
    const handleSupplierChange = async (supplierId) => {
        setSelectedSupplier(supplierId);
        setLoadingCertificates(true);
        
        try {
            console.log('🔍 Fetching certificates for supplier:', supplierId);
            
            const response = await apiClient.get(`/api/SupplierCertification/supplier/${supplierId}`);
            const certData = response.data;
            
            console.log('📜 Certificates loaded:', certData);
            
            setSupplierCertificates(certData);
            
            // ✅ Auto-fill certificate fields
            form.setFieldsValue({
                hasIATF16949: certData.hasIATF16949 || false,
                hasISO14001: certData.hasISO14001 || false,
            });
            
            // ✅ Show success message with certificate info
            const certMessages = [];
            if (certData.hasIATF16949) {
                certMessages.push(`✅ IATF 16949 (${t('valid_until')}: ${moment(certData.iatf16949ExpiryDate).format('DD/MM/YYYY')})`);
            } else {
                certMessages.push(`❌ IATF 16949 (${t('not_available')})`);
            }
            
            if (certData.hasISO14001) {
                certMessages.push(`✅ ISO 14001 (${t('valid_until')}: ${moment(certData.iso14001ExpiryDate).format('DD/MM/YYYY')})`);
            } else {
                certMessages.push(`❌ ISO 14001 (${t('not_available')})`);
            }
            
            message.success({
                content: (
                    <div>
                        <div style={{ fontWeight: '600', marginBottom: '8px' }}>{t('certificates_loaded')}</div>
                        {certMessages.map((msg, idx) => (
                            <div key={idx} style={{ fontSize: '12px' }}>{msg}</div>
                        ))}
                    </div>
                ),
                duration: 5
            });
            
        } catch (error) {
            console.error('Error fetching certificates:', error);
            
            // ✅ If no certificates found, set defaults
            setSupplierCertificates(null);
            form.setFieldsValue({
                hasIATF16949: false,
                hasISO14001: false,
            });
            
            message.info(t('no_certificates_found_for_supplier'));
        } finally {
            setLoadingCertificates(false);
        }
    };

    // ✅ Handle file upload
    const handleFileUpload = async (fieldName, file) => {
        if (!selectedSupplier) {
            message.error(t('please_select_supplier_first'));
            return false;
        }

        const period = form.getFieldValue('period');
        if (!period) {
            message.error(t('please_select_period_first'));
            return false;
        }

        setUploadingField(fieldName);

        try {
            // Store file temporarily
            setUploadedFiles(prev => ({
                ...prev,
                [fieldName]: [...(prev[fieldName] || []), file]
            }));

            message.success(t('file_uploaded_successfully'));
            return false; // Prevent default upload
        } catch (error) {
            message.error(t('file_upload_error'));
            return false;
        } finally {
            setUploadingField(null);
        }
    };

    // ✅ Remove uploaded file
    const handleFileRemove = (fieldName, file) => {
        setUploadedFiles(prev => ({
            ...prev,
            [fieldName]: (prev[fieldName] || []).filter(f => f.uid !== file.uid)
        }));
        message.success(t('file_removed'));
    };

    // ✅ Handle form submit
    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            const formattedDate = values.period.format('YYYY-MM');

            // ✅ Sevkiyat Lojistik Başarısı: Deadline + Damage + Documents toplamı,
            // mevcut backend alanı targetKPIAchievement üzerinden gönderiliyor (backend'de şema değişikliği yok).
            const shipmentLogisticsScore = getShipmentLogisticsScore(values);

            const payload = {
                supplierId: values.supplierId,
                period: formattedDate,
                productRevenue: values.productRevenue,
                productQuantity: values.productQuantity,
                defectProductQuantity: values.defectProductQuantity,
                targetPPM: values.targetPPM,
                returnCost: values.returnCost || 0,
                returnCostPaid: values.returnCostPaid || false,
                auditPerformance: values.auditPerformance,
                productSafety: values.productSafety,
                eSPICEDeviations: values.eSPICEDeviations,
                developedProjectsCount: values.developedProjectsCount,
                manufacturingMaturityCount: values.manufacturingMaturityCount,
                targetKPIAchievement: shipmentLogisticsScore,
                capaClosureDays: values.capaClosureDays,
                isNewSupplier: values.isNewSupplier || false,
                hasIATF16949: values.hasIATF16949 || false,
                hasISO14001: values.hasISO14001 || false
            };

            console.log('📤 Sending payload:', payload);

            const response = await apiClient.post('/api/SupplierPerformance', payload);
            console.log('✅ Response:', response.data);

            if (response.data) {
                const data = response.data;
                const performanceDataId = data.performanceDataId;

                // ✅ Upload files after performance data is created
                if (Object.keys(uploadedFiles).length > 0) {
                    await uploadFilesToPerformance(performanceDataId);
                }

                setCalculatedValues({
                    ppm: data.ppm?.toFixed(2),
                    totalScore: data.totalScore?.toFixed(2),
                    supplierClass: data.supplierClass,
                    auditFrequency: data.auditFrequency,
                    productQuantityRisk: data.riskAssessment?.productQuantityRisk,
                    ppmRisk: data.riskAssessment?.ppmRisk,
                    auditRisk: data.riskAssessment?.auditRisk,
                    productSafetyRisk: data.riskAssessment?.productSafetyRisk,
                    eSPICEDeviationsRisk: data.riskAssessment?.eSPICEDeviationsRisk,
                    developedProjectsRisk: data.riskAssessment?.developedProjectsRisk,
                    manufacturingMaturityRisk: data.riskAssessment?.manufacturingMaturityRisk,
                    kpiRisk: data.riskAssessment?.kpiRisk,
                    capaRisk: data.riskAssessment?.capaRisk,
                    totalRiskScore: data.riskAssessment?.totalRiskScore,
                    totalRiskLevel: data.riskAssessment?.totalRiskLevel
                });

                message.success(t('performance_data_created_successfully'));
            }
        } catch (error) {
            const errorMessage = handleApiError(error, t('error_saving_performance_data'));
            message.error(errorMessage);
            console.error('Error saving performance data:', error);
        } finally {
            setLoading(false);
        }
    };

    // ✅ Upload files to performance data
    const uploadFilesToPerformance = async (performanceDataId) => {
        const uploadPromises = [];

        for (const [fieldName, files] of Object.entries(uploadedFiles)) {
            for (const file of files) {
                const formData = new FormData();
                formData.append('file', file);
                formData.append('performanceDataId', performanceDataId);
                formData.append('fieldName', fieldName);
                formData.append('description', `${fieldName} document`);

                uploadPromises.push(
                    apiClient.post('/api/PerformanceAttachment/upload', formData, {
                        headers: { 'Content-Type': 'multipart/form-data' }
                    })
                );
            }
        }

        try {
            await Promise.all(uploadPromises);
            console.log('✅ All files uploaded successfully');
            setUploadedFiles({}); // Clear uploaded files
        } catch (error) {
            console.error('Error uploading files:', error);
            message.warning(t('some_files_failed_to_upload'));
        }
    };

    // ✅ Risk color helper
    const getRiskColor = (risk) => {
        if (risk === 1) return 'success';
        if (risk === 3) return 'warning';
        if (risk === 9) return 'error';
        return 'default';
    };

    const getRiskIcon = (risk) => {
        if (risk === 1) return <CheckCircleOutlined />;
        if (risk === 3) return <WarningOutlined />;
        if (risk === 9) return <CloseCircleOutlined />;
        return <InfoCircleOutlined />;
    };

    const getClassColor = (supplierClass) => {
        if (supplierClass === 'A') return '#52c41a';
        if (supplierClass === 'B') return '#faad14';
        if (supplierClass === 'C') return '#f5222d';
        return '#d9d9d9';
    };

    const getRiskLevelColor = (level) => {
        if (level === 'Low') return 'success';
        if (level === 'Medium') return 'warning';
        if (level === 'High') return 'error';
        return 'default';
    };

    // ✅ File upload component for each section
    const FileUploadSection = ({ fieldName, title }) => {
        const files = uploadedFiles[fieldName] || [];

        return (
            <div style={{ marginTop: '16px' }}>
                <Divider orientation="left" style={{ fontSize: '14px', color: '#8c8c8c' }}>
                    <PaperClipOutlined /> {t('attachments')}
                </Divider>
                
                <Upload
                    beforeUpload={(file) => handleFileUpload(fieldName, file)}
                    onRemove={(file) => handleFileRemove(fieldName, file)}
                    fileList={files}
                    multiple
                    maxCount={5}
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                >
                    <Button 
                        icon={<UploadOutlined />} 
                        loading={uploadingField === fieldName}
                        style={{ width: '100%' }}
                    >
                        {t('upload_document')}
                    </Button>
                </Upload>

                {files.length > 0 && (
                    <div style={{ marginTop: '8px', fontSize: '12px', color: '#52c41a' }}>
                        <CheckCircleOutlined /> {files.length} {t('file_ready_to_upload')}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div style={{ padding: '24px', backgroundColor: '#f0f2f5', minHeight: '100vh' }}>
            {/* Header */}
            <div style={{
                background: '#fff',
                padding: '32px',
                borderRadius: '12px',
                marginBottom: '24px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                borderLeft: '4px solid #1890ff'
            }}>
                <h1 style={{
                    fontSize: '32px',
                    fontWeight: '700',
                    color: '#262626',
                    margin: 0
                }}>
                    <DashboardOutlined style={{ marginRight: '12px', color: '#1890ff' }} />
                    {t('supplier_performance_data_management')}
                </h1>
                <p style={{ color: '#8c8c8c', margin: '8px 0 0 0', fontSize: '15px' }}>
                    {t('add_new_performance_data')}
                </p>
            </div>

            {/* Main Form Card */}
            <Card style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    initialValues={{
                        returnCost: 0,
                        returnCostPaid: false,
                        isNewSupplier: false,
                        hasIATF16949: false,
                        hasISO14001: false
                    }}
                >
                    {/* Basic Information - ✅ RENKSIZ */}
                    <Card
                        style={{
                            marginBottom: '24px',
                            background: '#fff',
                            border: '1px solid #d9d9d9',
                            borderRadius: '12px'
                        }}
                        bodyStyle={{ padding: '24px' }}
                    >
                        <Row gutter={16}>
                            <Col xs={24} sm={12} md={8}>
                                <Form.Item
                                    name="supplierId"
                                    label={<strong>{t('supplier')}</strong>}
                                    rules={[{ required: true, message: t('please_select_supplier') }]}
                                >
                                    <Select
                                        showSearch
                                        placeholder={t('select_supplier')}
                                        size="large"
                                        onChange={handleSupplierChange}
                                        loading={loadingCertificates}
                                        filterOption={(input, option) =>
                                            option.children.toLowerCase().includes(input.toLowerCase())
                                        }
                                    >
                                        {suppliers.map(supplier => (
                                            <Option key={supplier.supplierId} value={supplier.supplierId}>
                                                {supplier.supplierName}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={12} md={8}>
                                <Form.Item
                                    name="period"
                                    label={<strong>{t('period')}</strong>}
                                    rules={[{ required: true, message: t('please_select_period') }]}
                                >
                                    <DatePicker.MonthPicker
                                        format="YYYY-MM"
                                        style={{ width: '100%' }}
                                        size="large"
                                        placeholder={t('select_month')}
                                    />
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={24} md={8}>
                                <div style={{ marginTop: '30px' }}>
                                    <Button
                                        type="primary"
                                        size="large"
                                        icon={<SaveOutlined />}
                                        onClick={handleSubmit}
                                        loading={loading}
                                        style={{
                                            width: '100%',
                                            height: '48px',
                                            fontWeight: '600',
                                            fontSize: '16px'
                                        }}
                                    >
                                        {t('save_and_calculate')}
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </Card>

                    <Divider style={{ margin: '32px 0' }} />

                    {/* Collapsible Sections */}
                    <Collapse
                        bordered={false}
                        activeKey={activeKey}
                        onChange={setActiveKey}
                        expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
                        style={{
                            background: 'transparent',
                            border: 'none'
                        }}
                    >
                        {/* 1. Product & Quantity */}
                        <Panel
                            header={
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ fontSize: '16px', fontWeight: '600' }}>
                                        {t('product_quantity_risk')}
                                    </span>
                                    {calculatedValues?.productQuantityRisk && (
                                        <Tag color={getRiskColor(calculatedValues.productQuantityRisk)} icon={getRiskIcon(calculatedValues.productQuantityRisk)}>
                                            {t('risk')}: {calculatedValues.productQuantityRisk}
                                        </Tag>
                                    )}
                                </div>
                            }
                            key="1"
                            style={{
                                marginBottom: '16px',
                                background: '#fff',
                                borderRadius: '12px',
                                border: '1px solid #f0f0f0'
                            }}
                        >
                            <Row gutter={16}>
                                <Col xs={24} sm={12} md={8}>
                                    <Form.Item
                                        name="productRevenue"
                                        label={t('product_revenue_usd')}
                                        rules={[{ required: true, message: t('please_enter_product_revenue') }]}
                                    >
                                        <InputNumber
                                            style={{ width: '100%' }}
                                            min={0}
                                            placeholder={t('enter_product_revenue')}
                                            formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                            parser={value => value.replace(/\$\s?|(,*)/g, '')}
                                            size="large"
                                        />
                                    </Form.Item>
                                </Col>

                                <Col xs={24} sm={12} md={8}>
                                    <Form.Item
                                        name="productQuantity"
                                        label={t('product_quantity')}
                                        rules={[{ required: true, message: t('please_enter_product_quantity') }]}
                                    >
                                        <InputNumber
                                            style={{ width: '100%' }}
                                            min={0}
                                            placeholder={t('enter_product_quantity')}
                                            size="large"
                                        />
                                    </Form.Item>
                                </Col>

                                <Col xs={24} sm={12} md={8}>
                                    <Form.Item label={<span>{t('product_quantity_risk')} <Tag color="blue">{t('auto_calculated')}</Tag></span>}>
                                        {calculatedValues?.productQuantityRisk ? (
                                            <Tag
                                                color={getRiskColor(calculatedValues.productQuantityRisk)}
                                                icon={getRiskIcon(calculatedValues.productQuantityRisk)}
                                                style={{ fontSize: '16px', padding: '8px 16px' }}
                                            >
                                                {calculatedValues.productQuantityRisk}
                                            </Tag>
                                        ) : (
                                            <Tag color="default">-</Tag>
                                        )}
                                    </Form.Item>
                                </Col>
                            </Row>

                            <FileUploadSection fieldName="ProductQuantity" title={t('product_quantity_risk')} />
                        </Panel>

                        {/* 2. PPM & Quality */}
                        <Panel
                            header={
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ fontSize: '16px', fontWeight: '600' }}>
                                        {t('ppm_risk')}
                                    </span>
                                    {calculatedValues?.ppmRisk && (
                                        <Tag color={getRiskColor(calculatedValues.ppmRisk)} icon={getRiskIcon(calculatedValues.ppmRisk)}>
                                            PPM {t('risk')}: {calculatedValues.ppmRisk}
                                        </Tag>
                                    )}
                                </div>
                            }
                            key="2"
                            style={{
                                marginBottom: '16px',
                                background: '#fff',
                                borderRadius: '12px',
                                border: '1px solid #f0f0f0'
                            }}
                        >
                            <Row gutter={16}>
                                <Col xs={24} sm={12} md={6}>
                                    <Form.Item
                                        name="defectProductQuantity"
                                        label={t('defect_product_quantity')}
                                        rules={[{ required: true, message: t('please_enter_defect_quantity') }]}
                                    >
                                        <InputNumber
                                            style={{ width: '100%' }}
                                            min={0}
                                            placeholder={t('enter_defect_quantity')}
                                            size="large"
                                        />
                                    </Form.Item>
                                </Col>

                                <Col xs={24} sm={12} md={6}>
                                    <Form.Item label={<span>PPM <Tag color="blue">{t('auto_calculated')}</Tag></span>}>
                                        {calculatedValues?.ppm ? (
                                            <Tag color="processing" style={{ fontSize: '16px', padding: '8px 16px' }}>
                                                {calculatedValues.ppm}
                                            </Tag>
                                        ) : (
                                            <Tag color="default">-</Tag>
                                        )}
                                    </Form.Item>
                                </Col>

                                <Col xs={24} sm={12} md={6}>
                                    <Form.Item
                                        name="targetPPM"
                                        label={t('target_ppm')}
                                        rules={[{ required: true, message: t('please_enter_target_ppm') }]}
                                    >
                                        <InputNumber
                                            style={{ width: '100%' }}
                                            min={0}
                                            placeholder={t('enter_target_ppm')}
                                            size="large"
                                        />
                                    </Form.Item>
                                </Col>

                                <Col xs={24} sm={12} md={6}>
                                    <Form.Item label={<span>PPM {t('risk')} <Tag color="blue">⚙️</Tag></span>}>
                                        {calculatedValues?.ppmRisk ? (
                                            <Tag
                                                color={getRiskColor(calculatedValues.ppmRisk)}
                                                icon={getRiskIcon(calculatedValues.ppmRisk)}
                                                style={{ fontSize: '16px', padding: '8px 16px' }}
                                            >
                                                {calculatedValues.ppmRisk}
                                            </Tag>
                                        ) : (
                                            <Tag color="default">-</Tag>
                                        )}
                                    </Form.Item>
                                </Col>
                            </Row>

                            <FileUploadSection fieldName="PPMQuality" title={t('ppm_risk')} />
                        </Panel>

                        {/* 3. Cost & Audit */}
                        <Panel
                            header={
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ fontSize: '16px', fontWeight: '600' }}>
                                        {t('audit_risk')}
                                    </span>
                                    {calculatedValues?.auditRisk && (
                                        <Tag color={getRiskColor(calculatedValues.auditRisk)} icon={getRiskIcon(calculatedValues.auditRisk)}>
                                            {t('audit_risk')}: {calculatedValues.auditRisk}
                                        </Tag>
                                    )}
                                </div>
                            }
                            key="3"
                            style={{
                                marginBottom: '16px',
                                background: '#fff',
                                borderRadius: '12px',
                                border: '1px solid #f0f0f0'
                            }}
                        >
                            <Row gutter={16}>
                                <Col xs={24} sm={12} md={6}>
                                    <Form.Item
                                        name="returnCost"
                                        label={t('return_cost')}
                                    >
                                        <InputNumber
                                            style={{ width: '100%' }}
                                            min={0}
                                            placeholder={t('enter_return_cost')}
                                            formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                            parser={value => value.replace(/\$\s?|(,*)/g, '')}
                                            size="large"
                                        />
                                    </Form.Item>
                                </Col>

                                <Col xs={24} sm={12} md={6}>
                                    <Form.Item
                                        name="returnCostPaid"
                                        label={t('return_cost_paid')}
                                        valuePropName="checked"
                                    >
                                        <Switch checkedChildren={t('yes')} unCheckedChildren={t('no')} />
                                    </Form.Item>
                                </Col>

                                <Col xs={24} sm={12} md={6}>
                                    <Form.Item
                                        name="auditPerformance"
                                        label={t('audit_performance')}
                                        rules={[{ required: true, message: t('please_enter_audit_performance') }]}
                                    >
                                        <InputNumber
                                            style={{ width: '100%' }}
                                            min={0}
                                            max={100}
                                            placeholder={t('enter_audit_performance')}
                                            addonAfter="%"
                                            size="large"
                                        />
                                    </Form.Item>
                                </Col>

                                <Col xs={24} sm={12} md={6}>
                                    <Form.Item label={<span>{t('audit_risk')} <Tag color="blue">⚙️</Tag></span>}>
                                        {calculatedValues?.auditRisk ? (
                                            <Tag
                                                color={getRiskColor(calculatedValues.auditRisk)}
                                                icon={getRiskIcon(calculatedValues.auditRisk)}
                                                style={{ fontSize: '16px', padding: '8px 16px' }}
                                            >
                                                {calculatedValues.auditRisk}
                                            </Tag>
                                        ) : (
                                            <Tag color="default">-</Tag>
                                        )}
                                    </Form.Item>
                                </Col>
                            </Row>

                            <FileUploadSection fieldName="AuditCost" title={t('audit_risk')} />
                        </Panel>

                        {/* 4. Product Safety & e-SPICE */}
                        <Panel
                            header={
                                <span style={{ fontSize: '16px', fontWeight: '600' }}>
                                    {t('product_safety')} & e-SPICE
                                </span>
                            }
                            key="4"
                            style={{
                                marginBottom: '16px',
                                background: '#fff',
                                borderRadius: '12px',
                                border: '1px solid #f0f0f0'
                            }}
                        >
                            <Row gutter={16}>
                                <Col xs={24} sm={12} md={8}>
                                    <Form.Item
                                        name="productSafety"
                                        label={t('product_safety')}
                                        rules={[{ required: true, message: t('please_select_product_safety') }]}
                                    >
                                        <Select placeholder={t('select_product_safety')} size="large">
                                            <Option value={1}>
                                                <Tag color="success" icon={<CheckCircleOutlined />}>1 - {t('low_risk')}</Tag>
                                            </Option>
                                            <Option value={9}>
                                                <Tag color="error" icon={<CloseCircleOutlined />}>9 - {t('high_risk')}</Tag>
                                            </Option>
                                        </Select>
                                    </Form.Item>
                                </Col>

                                <Col xs={24} sm={12} md={8}>
                                    <Form.Item
                                        name="eSPICEDeviations"
                                        label={t('e_spice_deviations')}
                                        rules={[{ required: true, message: t('please_select_e_spice') }]}
                                    >
                                        <Select placeholder={t('select_e_spice')} size="large">
                                            <Option value={1}>
                                                <Tag color="success" icon={<CheckCircleOutlined />}>1 - {t('low_risk')}</Tag>
                                            </Option>
                                            <Option value={9}>
                                                <Tag color="error" icon={<CloseCircleOutlined />}>9 - {t('high_risk')}</Tag>
                                            </Option>
                                        </Select>
                                    </Form.Item>
                                </Col>

                                <Col xs={24} sm={12} md={8}>
                                    <Form.Item
                                        name="developedProjectsCount"
                                        label={t('developed_projects_count')}
                                        rules={[{ required: true, message: t('please_enter_projects_count') }]}
                                    >
                                        <InputNumber
                                            style={{ width: '100%' }}
                                            min={0}
                                            placeholder={t('enter_projects_count')}
                                            size="large"
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Row gutter={16}>
                                <Col xs={24} sm={12} md={8}>
                                    <Form.Item label={<span>{t('developed_projects_risk')} <Tag color="blue">⚙️</Tag></span>}>
                                        {calculatedValues?.developedProjectsRisk ? (
                                            <Tag
                                                color={getRiskColor(calculatedValues.developedProjectsRisk)}
                                                icon={getRiskIcon(calculatedValues.developedProjectsRisk)}
                                                style={{ fontSize: '16px', padding: '8px 16px' }}
                                            >
                                                {calculatedValues.developedProjectsRisk}
                                            </Tag>
                                        ) : (
                                            <Tag color="default">-</Tag>
                                        )}
                                    </Form.Item>
                                </Col>
                            </Row>

                            <FileUploadSection fieldName="ProductSafety" title={t('product_safety')} />
                        </Panel>

                        {/* 5. Manufacturing Maturity */}
                        <Panel
                            header={
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ fontSize: '16px', fontWeight: '600' }}>
                                        {t('manufacturing_maturity_risk')}
                                    </span>
                                    {calculatedValues?.manufacturingMaturityRisk && (
                                        <Tag color={getRiskColor(calculatedValues.manufacturingMaturityRisk)} icon={getRiskIcon(calculatedValues.manufacturingMaturityRisk)}>
                                            {t('risk')}: {calculatedValues.manufacturingMaturityRisk}
                                        </Tag>
                                    )}
                                </div>
                            }
                            key="5"
                            style={{
                                marginBottom: '16px',
                                background: '#fff',
                                borderRadius: '12px',
                                border: '1px solid #f0f0f0'
                            }}
                        >
                            <Row gutter={16}>
                                <Col xs={24} sm={12} md={12}>
                                    <Form.Item
                                        name="manufacturingMaturityCount"
                                        label={
                                            <span>
                                                {t('manufacturing_maturity_count')}
                                                <Tooltip title={t('range_0_65')}>
                                                    <InfoCircleOutlined style={{ marginLeft: '8px', color: '#1890ff' }} />
                                                </Tooltip>
                                            </span>
                                        }
                                        rules={[{ required: true, message: t('please_enter_maturity_count') }]}
                                        extra={t('range_0_65')}
                                    >
                                        <InputNumber
                                            style={{ width: '100%' }}
                                            min={0}
                                            max={65}
                                            placeholder={t('enter_maturity_count')}
                                            size="large"
                                        />
                                    </Form.Item>
                                </Col>

                                <Col xs={24} sm={12} md={12}>
                                    <Form.Item label={<span>{t('manufacturing_maturity_risk')} <Tag color="blue">⚙️</Tag></span>}>
                                        {calculatedValues?.manufacturingMaturityRisk ? (
                                            <Tag
                                                color={getRiskColor(calculatedValues.manufacturingMaturityRisk)}
                                                icon={getRiskIcon(calculatedValues.manufacturingMaturityRisk)}
                                                style={{ fontSize: '16px', padding: '8px 16px' }}
                                            >
                                                {calculatedValues.manufacturingMaturityRisk}
                                            </Tag>
                                        ) : (
                                            <Tag color="default">-</Tag>
                                        )}
                                    </Form.Item>
                                </Col>
                            </Row>

                            <FileUploadSection fieldName="ManufacturingMaturity" title={t('manufacturing_maturity_risk')} />
                        </Panel>

                        {/* 6. KPI & CAPA */}
                        <Panel
                            header={
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ fontSize: '16px', fontWeight: '600' }}>
                                        KPI & CAPA
                                    </span>
                                    <Space>
                                        {calculatedValues?.kpiRisk && (
                                            <Tag color={getRiskColor(calculatedValues.kpiRisk)} icon={getRiskIcon(calculatedValues.kpiRisk)}>
                                                KPI: {calculatedValues.kpiRisk}
                                            </Tag>
                                        )}
                                        {calculatedValues?.capaRisk && (
                                            <Tag color={getRiskColor(calculatedValues.capaRisk)} icon={getRiskIcon(calculatedValues.capaRisk)}>
                                                CAPA: {calculatedValues.capaRisk}
                                            </Tag>
                                        )}
                                    </Space>
                                </div>
                            }
                            key="6"
                            style={{
                                marginBottom: '16px',
                                background: '#fff',
                                borderRadius: '12px',
                                border: '1px solid #f0f0f0'
                            }}
                        >
                            <Row gutter={16}>
                                <Col xs={24} sm={12} md={8}>
                                    <Form.Item label={<span>KPI {t('risk')} <Tag color="blue">⚙️</Tag></span>}>
                                        {calculatedValues?.kpiRisk ? (
                                            <Tag
                                                color={getRiskColor(calculatedValues.kpiRisk)}
                                                icon={getRiskIcon(calculatedValues.kpiRisk)}
                                                style={{ fontSize: '16px', padding: '8px 16px' }}
                                            >
                                                {calculatedValues.kpiRisk}
                                            </Tag>
                                        ) : (
                                            <Tag color="default">-</Tag>
                                        )}
                                    </Form.Item>
                                </Col>
                            </Row>

                            {/* ✅ Sevkiyat Lojistik Başarısı - Deadline / Damage / Documents (paylaşılan bileşen) */}
                            <ShipmentLogisticsScoreFields form={form} />

                            <FileUploadSection fieldName="ShipmentLogistics" title={t('shipment_logistics_documents')} />

                            <Row gutter={16}>
                                <Col xs={24} sm={12} md={8}>
                                    <Form.Item
                                        name="capaClosureDays"
                                        label={t('capa_closure_days')}
                                        rules={[{ required: true, message: t('please_enter_capa_days') }]}
                                    >
                                        <InputNumber
                                            style={{ width: '100%' }}
                                            min={0}
                                            placeholder={t('enter_capa_days')}
                                            addonAfter={t('days')}
                                            size="large"
                                        />
                                    </Form.Item>
                                </Col>

                                <Col xs={24} sm={12} md={8}>
                                    <Form.Item label={<span>CAPA {t('risk')} <Tag color="blue">⚙️</Tag></span>}>
                                        {calculatedValues?.capaRisk ? (
                                            <Tag
                                                color={getRiskColor(calculatedValues.capaRisk)}
                                                icon={getRiskIcon(calculatedValues.capaRisk)}
                                                style={{ fontSize: '16px', padding: '8px 16px' }}
                                            >
                                                {calculatedValues.capaRisk}
                                            </Tag>
                                        ) : (
                                            <Tag color="default">-</Tag>
                                        )}
                                    </Form.Item>
                                </Col>
                            </Row>

                            <FileUploadSection fieldName="KPICAPA" title="KPI & CAPA" />
                        </Panel>

                        {/* 7. Additional Info - ✅ OTOMATİK SERTİFİKA DOLDURMA */}
                        <Panel
                            header={
                                <span style={{ fontSize: '16px', fontWeight: '600' }}>
                                    {t('additional_information')}
                                </span>
                            }
                            key="7"
                            style={{
                                marginBottom: '16px',
                                background: '#fff',
                                borderRadius: '12px',
                                border: '1px solid #f0f0f0'
                            }}
                        >
                            <Row gutter={16}>
                                <Col xs={24} sm={12} md={8}>
                                    <Form.Item
                                        name="isNewSupplier"
                                        label={t('new_supplier')}
                                        valuePropName="checked"
                                    >
                                        <Switch checkedChildren={t('yes')} unCheckedChildren={t('no')} />
                                    </Form.Item>
                                </Col>

                                <Col xs={24} sm={12} md={8}>
                                    <Form.Item
                                        name="hasIATF16949"
                                        label={
                                            <span>
                                                {t('iatf_16949')}
                                                <Tooltip title={t('auto_filled_from_certificates')}>
                                                    <InfoCircleOutlined style={{ marginLeft: '8px', color: '#1890ff' }} />
                                                </Tooltip>
                                            </span>
                                        }
                                        valuePropName="checked"
                                    >
                                        <Switch 
                                            checkedChildren={t('yes')} 
                                            unCheckedChildren={t('no')}
                                            disabled={supplierCertificates !== null}
                                        />
                                    </Form.Item>
                                    {supplierCertificates?.hasIATF16949 && supplierCertificates?.iatf16949ExpiryDate && (
                                        <div style={{ fontSize: '12px', color: '#52c41a', marginTop: '-16px', marginBottom: '16px' }}>
                                            <CheckCircleOutlined /> {t('valid_until')}: {moment(supplierCertificates.iatf16949ExpiryDate).format('DD/MM/YYYY')}
                                        </div>
                                    )}
                                    {supplierCertificates && !supplierCertificates.hasIATF16949 && (
                                        <div style={{ fontSize: '12px', color: '#ff4d4f', marginTop: '-16px', marginBottom: '16px' }}>
                                            <CloseCircleOutlined /> {t('not_available')}
                                        </div>
                                    )}
                                </Col>

                                <Col xs={24} sm={12} md={8}>
                                    <Form.Item
                                        name="hasISO14001"
                                        label={
                                            <span>
                                                {t('iso_14001')}
                                                <Tooltip title={t('auto_filled_from_certificates')}>
                                                    <InfoCircleOutlined style={{ marginLeft: '8px', color: '#1890ff' }} />
                                                </Tooltip>
                                            </span>
                                        }
                                        valuePropName="checked"
                                    >
                                        <Switch 
                                            checkedChildren={t('yes')} 
                                            unCheckedChildren={t('no')}
                                            disabled={supplierCertificates !== null}
                                        />
                                    </Form.Item>
                                    {supplierCertificates?.hasISO14001 && supplierCertificates?.iso14001ExpiryDate && (
                                        <div style={{ fontSize: '12px', color: '#52c41a', marginTop: '-16px', marginBottom: '16px' }}>
                                            <CheckCircleOutlined /> {t('valid_until')}: {moment(supplierCertificates.iso14001ExpiryDate).format('DD/MM/YYYY')}
                                        </div>
                                    )}
                                    {supplierCertificates && !supplierCertificates.hasISO14001 && (
                                        <div style={{ fontSize: '12px', color: '#ff4d4f', marginTop: '-16px', marginBottom: '16px' }}>
                                            <CloseCircleOutlined /> {t('not_available')}
                                        </div>
                                    )}
                                </Col>
                            </Row>

                            {/* ✅ Certificate Info Alert */}
                            {supplierCertificates && (
                                <Alert
                                    message={
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            <CheckCircleOutlined style={{ fontSize: '16px', marginRight: '8px' }} />
                                            <strong>{t('certificates_auto_loaded')}</strong>
                                        </div>
                                    }
                                    description={
                                        <div style={{ marginTop: '8px' }}>
                                            <Row gutter={16}>
                                                <Col span={12}>
                                                    <div style={{ 
                                                        padding: '12px', 
                                                        background: supplierCertificates.hasIATF16949 ? '#f6ffed' : '#fff1f0',
                                                        borderRadius: '8px',
                                                        border: `1px solid ${supplierCertificates.hasIATF16949 ? '#b7eb8f' : '#ffccc7'}`
                                                    }}>
                                                        <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                                                            {supplierCertificates.hasIATF16949 ? '✅' : '❌'} IATF 16949
                                                        </div>
                                                        {supplierCertificates.hasIATF16949 ? (
                                                            <div style={{ fontSize: '12px', color: '#52c41a' }}>
                                                                {t('valid_until')}: {moment(supplierCertificates.iatf16949ExpiryDate).format('DD/MM/YYYY')}
                                                            </div>
                                                        ) : (
                                                            <div style={{ fontSize: '12px', color: '#ff4d4f' }}>
                                                                {t('not_available')}
                                                            </div>
                                                        )}
                                                    </div>
                                                </Col>
                                                <Col span={12}>
                                                    <div style={{ 
                                                        padding: '12px', 
                                                        background: supplierCertificates.hasISO14001 ? '#f6ffed' : '#fff1f0',
                                                        borderRadius: '8px',
                                                        border: `1px solid ${supplierCertificates.hasISO14001 ? '#b7eb8f' : '#ffccc7'}`
                                                    }}>
                                                        <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                                                            {supplierCertificates.hasISO14001 ? '✅' : '❌'} ISO 14001
                                                        </div>
                                                        {supplierCertificates.hasISO14001 ? (
                                                            <div style={{ fontSize: '12px', color: '#52c41a' }}>
                                                                {t('valid_until')}: {moment(supplierCertificates.iso14001ExpiryDate).format('DD/MM/YYYY')}
                                                            </div>
                                                        ) : (
                                                            <div style={{ fontSize: '12px', color: '#ff4d4f' }}>
                                                                {t('not_available')}
                                                            </div>
                                                        )}
                                                    </div>
                                                </Col>
                                            </Row>
                                        </div>
                                    }
                                    type="info"
                                    showIcon
                                    style={{ marginTop: '16px', borderRadius: '12px' }}
                                />
                            )}

                            {!supplierCertificates && selectedSupplier && (
                                <Alert
                                    message={t('no_certificates_found')}
                                    description={t('please_add_certificates_first')}
                                    type="warning"
                                    showIcon
                                    style={{ marginTop: '16px', borderRadius: '12px' }}
                                />
                            )}

                            <FileUploadSection fieldName="AdditionalInfo" title={t('additional_information')} />
                        </Panel>
                    </Collapse>
                </Form>
            </Card>

            {/* Results Summary */}
            {calculatedValues && (
                <>
                    {/* ✅ 1. SERTİFİKA UYARISI (Varsa) */}
                    {(!form.getFieldValue('hasIATF16949') || !form.getFieldValue('hasISO14001')) && (
                        <Alert
                            type="warning"
                            showIcon
                            icon={<WarningOutlined style={{ fontSize: '20px' }} />}
                            style={{
                                marginTop: '24px',
                                borderRadius: '12px',
                                border: '2px solid #faad14'
                            }}
                            message={
                                <div style={{ fontSize: '16px', fontWeight: '600' }}>
                                    {t('certificate_warning_title')}
                                </div>
                            }
                            description={
                                <div style={{ fontSize: '14px', marginTop: '8px' }}>
                                    {!form.getFieldValue('hasIATF16949') && !form.getFieldValue('hasISO14001') && (
                                        <div style={{ marginBottom: '8px' }}>
                                            <CloseCircleOutlined style={{ color: '#ff4d4f', marginRight: '8px' }} />
                                            <strong>{t('max_score_limited_to')} 69</strong> - {t('missing_both_certificates')}
                                        </div>
                                    )}
                                    {form.getFieldValue('hasIATF16949') && !form.getFieldValue('hasISO14001') && (
                                        <div style={{ marginBottom: '8px' }}>
                                            <WarningOutlined style={{ color: '#faad14', marginRight: '8px' }} />
                                            <strong>{t('max_score_limited_to')} 79</strong> - {t('missing_iso_14001')}
                                        </div>
                                    )}
                                    <div style={{ marginTop: '12px', padding: '12px', background: '#fffbe6', borderRadius: '8px' }}>
                                        <InfoCircleOutlined style={{ marginRight: '8px', color: '#faad14' }} />
                                        {t('certificate_recommendation')}
                                    </div>
                                </div>
                            }
                        />
                    )}

                    {/* ✅ 2. SCORE BREAKDOWN CARD */}
                    <Card
                        style={{
                            marginTop: '24px',
                            borderRadius: '12px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                            borderLeft: '4px solid #722ed1'
                        }}
                    >
                        <div style={{ marginBottom: '24px' }}>
                            <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: '#262626' }}>
                                <RiseOutlined style={{ marginRight: '12px', color: '#722ed1' }} />
                                {t('score_breakdown')}
                            </h2>
                        </div>

                        <Row gutter={[16, 16]}>
                            {/* Base Score */}
                            <Col xs={24} sm={12} md={8}>
                                <Card style={{ background: '#f0f5ff', border: '1px solid #adc6ff', borderRadius: '12px' }}>
                                    <div style={{ textAlign: 'center' }}>
                                        <div style={{ fontSize: '14px', color: '#8c8c8c', marginBottom: '8px' }}>
                                            {t('base_score')}
                                        </div>
                                        <div style={{ fontSize: '32px', fontWeight: '700', color: '#1890ff' }}>
                                            {(calculatedValues.totalScore - 
                                            (calculatedValues.riskAssessment?.ppmRisk === 1 ? 15 : 0) -
                                            (form.getFieldValue('isNewSupplier') ? -8 : 0) -
                                            (form.getFieldValue('returnCost') > 1000 ? (form.getFieldValue('returnCostPaid') ? -5 : -10) : 0)
                                            ).toFixed(2)}
                                        </div>
                                        <div style={{ fontSize: '12px', color: '#8c8c8c', marginTop: '4px' }}>
                                            {t('weighted_risk_scores')}
                                        </div>
                                    </div>
                                </Card>
                            </Col>

                            {/* PPM Bonus */}
                            <Col xs={24} sm={12} md={8}>
                                <Card style={{ 
                                    background: calculatedValues.riskAssessment?.ppmRisk === 1 ? '#f6ffed' : '#fafafa', 
                                    border: `1px solid ${calculatedValues.riskAssessment?.ppmRisk === 1 ? '#b7eb8f' : '#d9d9d9'}`,
                                    borderRadius: '12px' 
                                }}>
                                    <div style={{ textAlign: 'center' }}>
                                        <div style={{ fontSize: '14px', color: '#8c8c8c', marginBottom: '8px' }}>
                                            {t('ppm_bonus')}
                                        </div>
                                        <div style={{ fontSize: '32px', fontWeight: '700', color: calculatedValues.riskAssessment?.ppmRisk === 1 ? '#52c41a' : '#d9d9d9' }}>
                                            {calculatedValues.riskAssessment?.ppmRisk === 1 ? '+15' : '0'}
                                        </div>
                                        <div style={{ fontSize: '12px', color: '#8c8c8c', marginTop: '4px' }}>
                                            {calculatedValues.riskAssessment?.ppmRisk === 1 ? t('target_achieved') : t('target_not_achieved')}
                                        </div>
                                    </div>
                                </Card>
                            </Col>

                            {/* Certificate Limit */}
                            <Col xs={24} sm={12} md={8}>
                                <Card style={{ 
                                    background: (!form.getFieldValue('hasIATF16949') || !form.getFieldValue('hasISO14001')) ? '#fff1f0' : '#f6ffed',
                                    border: `1px solid ${(!form.getFieldValue('hasIATF16949') || !form.getFieldValue('hasISO14001')) ? '#ffccc7' : '#b7eb8f'}`,
                                    borderRadius: '12px' 
                                }}>
                                    <div style={{ textAlign: 'center' }}>
                                        <div style={{ fontSize: '14px', color: '#8c8c8c', marginBottom: '8px' }}>
                                            {t('max_score_limit')}
                                        </div>
                                        <div style={{ fontSize: '32px', fontWeight: '700', color: (!form.getFieldValue('hasIATF16949') || !form.getFieldValue('hasISO14001')) ? '#ff4d4f' : '#52c41a' }}>
                                            {!form.getFieldValue('hasIATF16949') ? '69' : !form.getFieldValue('hasISO14001') ? '79' : '100'}
                                        </div>
                                        <div style={{ fontSize: '12px', color: '#8c8c8c', marginTop: '4px' }}>
                                            {!form.getFieldValue('hasIATF16949') && !form.getFieldValue('hasISO14001') ? t('no_certificates') :
                                            !form.getFieldValue('hasIATF16949') ? t('missing_iatf') :
                                            !form.getFieldValue('hasISO14001') ? t('missing_iso') :
                                            t('all_certificates')}
                                        </div>
                                    </div>
                                </Card>
                            </Col>

                            {/* New Supplier Penalty */}
                            {form.getFieldValue('isNewSupplier') && (
                                <Col xs={24} sm={12} md={8}>
                                    <Card style={{ background: '#fff1f0', border: '1px solid #ffccc7', borderRadius: '12px' }}>
                                        <div style={{ textAlign: 'center' }}>
                                            <div style={{ fontSize: '14px', color: '#8c8c8c', marginBottom: '8px' }}>
                                                {t('new_supplier_penalty')}
                                            </div>
                                            <div style={{ fontSize: '32px', fontWeight: '700', color: '#ff4d4f' }}>
                                                -8
                                            </div>
                                            <div style={{ fontSize: '12px', color: '#8c8c8c', marginTop: '4px' }}>
                                                {t('first_year_penalty')}
                                            </div>
                                        </div>
                                    </Card>
                                </Col>
                            )}

                            {/* Return Cost Penalty */}
                            {form.getFieldValue('returnCost') > 1000 && (
                                <Col xs={24} sm={12} md={8}>
                                    <Card style={{ background: '#fff1f0', border: '1px solid #ffccc7', borderRadius: '12px' }}>
                                        <div style={{ textAlign: 'center' }}>
                                            <div style={{ fontSize: '14px', color: '#8c8c8c', marginBottom: '8px' }}>
                                                {t('return_cost_penalty')}
                                            </div>
                                            <div style={{ fontSize: '32px', fontWeight: '700', color: '#ff4d4f' }}>
                                                {form.getFieldValue('returnCostPaid') ? '-5' : '-10'}
                                            </div>
                                            <div style={{ fontSize: '12px', color: '#8c8c8c', marginTop: '4px' }}>
                                                {form.getFieldValue('returnCostPaid') ? t('partially_paid') : t('not_paid')}
                                            </div>
                                        </div>
                                    </Card>
                                </Col>
                            )}
                        </Row>
                    </Card>

                    {/* ✅ 3. MAIN RESULTS CARD */}
                    <Card
                        style={{
                            marginTop: '24px',
                            borderRadius: '12px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                            borderLeft: '4px solid #52c41a'
                        }}
                    >
                        <div style={{ marginBottom: '24px' }}>
                            <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: '#262626' }}>
                                <TrophyOutlined style={{ marginRight: '12px', color: '#52c41a' }} />
                                {t('evaluation_results')}
                            </h2>
                        </div>

                        <Row gutter={[16, 16]}>
                            <Col xs={24} sm={12} md={6}>
                                <Card style={{ background: '#f0f5ff', border: '1px solid #adc6ff', borderRadius: '12px' }}>
                                    <Statistic
                                        title={<strong>{t('total_score')}</strong>}
                                        value={calculatedValues.totalScore}
                                        suffix="/ 100"
                                        valueStyle={{ color: '#1890ff', fontSize: '32px', fontWeight: '700' }}
                                        prefix={<RiseOutlined />}
                                    />
                                </Card>
                            </Col>

                            <Col xs={24} sm={12} md={6}>
                                <Card style={{
                                    background: calculatedValues.supplierClass === 'A' ? '#f6ffed' :
                                        calculatedValues.supplierClass === 'B' ? '#fffbe6' : '#fff1f0',
                                    border: `1px solid ${getClassColor(calculatedValues.supplierClass)}`,
                                    borderRadius: '12px'
                                }}>
                                    <Statistic
                                        title={<strong>{t('supplier_class')}</strong>}
                                        value={calculatedValues.supplierClass}
                                        valueStyle={{
                                            color: getClassColor(calculatedValues.supplierClass),
                                            fontSize: '32px',
                                            fontWeight: '700'
                                        }}
                                        prefix={<SafetyOutlined />}
                                    />
                                </Card>
                            </Col>

                            <Col xs={24} sm={12} md={6}>
                                <Card style={{ background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: '12px' }}>
                                    <Statistic
                                        title={<strong>{t('audit_frequency')}</strong>}
                                        value={calculatedValues.auditFrequency}
                                        suffix="x/year"
                                        valueStyle={{ color: '#52c41a', fontSize: '32px', fontWeight: '700' }}
                                        prefix={<CheckCircleOutlined />}
                                    />
                                </Card>
                            </Col>

                            <Col xs={24} sm={12} md={6}>
                                <Card style={{
                                    background: calculatedValues.totalRiskLevel === 'Low' ? '#f6ffed' :
                                        calculatedValues.totalRiskLevel === 'Medium' ? '#fffbe6' : '#fff1f0',
                                    border: `1px solid ${calculatedValues.totalRiskLevel === 'Low' ? '#b7eb8f' :
                                        calculatedValues.totalRiskLevel === 'Medium' ? '#ffe58f' : '#ffccc7'}`,
                                    borderRadius: '12px'
                                }}>
                                    <div style={{ color: '#8c8c8c', fontSize: '14px', marginBottom: '8px', fontWeight: '600' }}>
                                        {t('total_risk_level')}
                                    </div>
                                    <Tag
                                        color={getRiskLevelColor(calculatedValues.totalRiskLevel)}
                                        style={{ fontSize: '18px', padding: '8px 16px', fontWeight: '700' }}
                                    >
                                        {calculatedValues.totalRiskLevel} ({calculatedValues.totalRiskScore})
                                    </Tag>
                                </Card>
                            </Col>
                        </Row>

                        <Divider />

                        {/* ✅ 4. AUDIT FREQUENCY EXPLANATION */}
                        <Alert
                            type="info"
                            showIcon
                            icon={<InfoCircleOutlined style={{ fontSize: '20px' }} />}
                            style={{ marginBottom: '16px', borderRadius: '12px' }}
                            message={<strong>{t('audit_frequency_calculation')}</strong>}
                            description={
                                <div style={{ fontSize: '14px' }}>
                                    <p style={{ margin: '8px 0' }}>
                                        <strong>{t('base_frequency')}:</strong> {
                                            calculatedValues.totalRiskScore >= 40 ? '4x/year (High Risk)' :
                                            calculatedValues.totalRiskScore >= 25 ? '3x/year (Medium-High Risk)' :
                                            calculatedValues.totalRiskScore >= 16 ? '2x/year (Medium Risk)' :
                                            '1x/year (Low Risk)'
                                        }
                                    </p>
                                    {form.getFieldValue('auditPerformance') < 70 && (
                                        <p style={{ margin: '8px 0', color: '#ff4d4f' }}>
                                            <WarningOutlined /> <strong>{t('audit_penalty')}:</strong> +1 ({t('audit_performance_below_70')})
                                        </p>
                                    )}
                                    {form.getFieldValue('isNewSupplier') && (
                                        <p style={{ margin: '8px 0', color: '#1890ff' }}>
                                            <InfoCircleOutlined /> <strong>{t('new_supplier_rule')}:</strong> {t('minimum_2_audits_per_year')}
                                        </p>
                                    )}
                                </div>
                            }
                        />

                        {/* ✅ 5. SUCCESS MESSAGE */}
                        <Alert
                            message={<strong>{t('performance_data_created_successfully')}</strong>}
                            description={
                                <div style={{ fontSize: '14px' }}>
                                    <p style={{ margin: '8px 0' }}>✅ <strong>PPM:</strong> {calculatedValues.ppm}</p>
                                    <p style={{ margin: '8px 0' }}>✅ <strong>{t('total_score')}:</strong> {calculatedValues.totalScore}</p>
                                    <p style={{ margin: '8px 0' }}>✅ <strong>{t('supplier_class')}:</strong> {calculatedValues.supplierClass}</p>
                                    <p style={{ margin: '8px 0' }}>✅ <strong>{t('total_risk_level')}:</strong> {calculatedValues.totalRiskLevel} ({calculatedValues.totalRiskScore})</p>
                                </div>
                            }
                            type="success"
                            showIcon
                            icon={<CheckCircleOutlined style={{ fontSize: '24px' }} />}
                            style={{ borderRadius: '12px' }}
                        />
                    </Card>
                </>
            )}
        </div>
    );
};

export default SupplierPerformanceDataEntry;
